import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api/index';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCartItems = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCartItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.get('/api/exchange/cart');
      const items = Array.isArray(data) ? data.map(item => ({
        id: item.id,
        productId: item.productId,
        name: item.name || '',
        imageUrl: item.imageUrl || '',
        description: item.description || '',
        price: item.price || 0,
        quantity: item.quantity || 1,
        selected: item.selected !== false,
        productStatus: item.productStatus || 'available',
        stock: item.stock || 0
      })) : [];
      setCartItems(items);
    } catch (error) {
      console.error('获取购物车失败:', error);
      setCartItems([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCartItems();
  }, [fetchCartItems]);

  const addToCart = async (item) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await api.post('/api/exchange/cart', {
        productId: item.productId || item.id,
        quantity: 1
      });
      await fetchCartItems();
    } catch (error) {
      console.error('添加购物车失败:', error);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      await api.delete(`/api/exchange/cart/${productId}`);
      await fetchCartItems();
    } catch (error) {
      console.error('删除购物车商品失败:', error);
    }
  };

  const removeMultipleFromCart = async (productIds) => {
    try {
      await api.post('/api/exchange/cart/remove-batch', { productIds });
      await fetchCartItems();
    } catch (error) {
      console.error('批量删除购物车商品失败:', error);
    }
  };

  const toggleSelect = async (productId) => {
    try {
      await api.put('/api/exchange/cart/toggle', { productId });
      await fetchCartItems();
    } catch (error) {
      console.error('切换选择状态失败:', error);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (quantity < 1) return;
    try {
      await api.put('/api/exchange/cart/quantity', { productId, quantity });
      await fetchCartItems();
    } catch (error) {
      console.error('更新数量失败:', error);
    }
  };

  const clearCart = async () => {
    try {
      await api.delete('/api/exchange/cart/clear');
      await fetchCartItems();
    } catch (error) {
      console.error('清空购物车失败:', error);
    }
  };

  const getSelectedItems = () => {
    return cartItems.filter(item => item.selected && item.productStatus !== 'off-stock' && item.productStatus !== 'deleted');
  };

  const getTotalPrice = () => {
    return getSelectedItems()
      .reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getAvailableItems = () => {
    return cartItems.filter(item => item.productStatus !== 'off-stock' && item.productStatus !== 'deleted');
  };

  const getOffStockItems = () => {
    return cartItems.filter(item => item.productStatus === 'off-stock' || item.productStatus === 'deleted');
  };

  const value = {
    cartItems,
    loading,
    addToCart,
    removeFromCart,
    removeMultipleFromCart,
    toggleSelect,
    updateQuantity,
    clearCart,
    getSelectedItems,
    getTotalPrice,
    getTotalCount,
    getAvailableItems,
    getOffStockItems,
    refreshCart: fetchCartItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

export default CartContext;
